type CarouselProps = {
  controllerPrev: HTMLButtonElement;
  controllerNext: HTMLButtonElement;
  listClassName: string;
  trackNode: HTMLElement;
  animationTimeSeconds?: number;
};

type CarouselState = {
  slideIndex: number;
  offset: number;
  isFirstSlide: boolean;
  isLastSlide: boolean;
};

export class Carousel {
  private static readonly defaultAnimationTime = 0.3;

  private controllerPrev: HTMLButtonElement;

  private controllerNext: HTMLButtonElement;

  private listNode: HTMLElement;

  private listItemsNodes: HTMLCollectionOf<Element>;

  private trackNode: HTMLElement;

  private slidesCount: number;

  private state: CarouselState;

  private readonly listClassName: string;

  private readonly animationTime: number;

  constructor(props: CarouselProps) {
    this.controllerPrev = props.controllerPrev;
    this.controllerNext = props.controllerNext;
    this.listClassName = props.listClassName;
    this.trackNode = props.trackNode;
    this.animationTime = props.animationTimeSeconds || Carousel.defaultAnimationTime;

    this.state = {
      slideIndex: 0,
      offset: 0,
      isFirstSlide: true,
      isLastSlide: false,
    };

    this.updateListNode();
    this.updateSlides();
    this.updateSlidesCount();
    this.update();

    if (window._?.throttle) {
      window.addEventListener('resize', window._.throttle(() => {
        this.setScrollAnimation(false);
        this.setState({
          slideIndex: 0,
          offset: 0,
          isFirstSlide: true,
          isLastSlide: false,
        });
        this.update();
      }, 300));
    }
  }

  get trackNodeWidth() {
    return this.trackNode.clientWidth;
  }

  get listItemWidth() {
    return this.listItemsNodes[0].clientWidth;
  }

  get slideMargin() {
    return parseInt(getComputedStyle(this.listItemsNodes[0]).marginRight, 10) || 0;
  }

  get fullVisibleSlidesOnSegmentCount() {
    return Math.floor(this.trackNodeWidth / (this.listItemWidth + this.slideMargin));
  }

  get visibleSlidesOnSegmentCount() {
    return Math.ceil(this.trackNodeWidth / (this.listItemWidth + this.slideMargin));
  }

  get listItemsCount() {
    return this.listItemsNodes.length;
  }

  get penultimateSegmentIndex() {
    return this.slidesCount - 2;
  }

  init = () => {
    this.initControllerPrevEventListener();
    this.initControllerNextEventListener();
  };

  initControllerPrevEventListener = () => {
    this.controllerPrev.addEventListener('click', this.scrollToLeft);
  };

  initControllerNextEventListener = () => {
    this.controllerNext.addEventListener('click', this.scrollToRight);
  };

  scrollToRight = () => {
    this.setScrollAnimation(true);
    this.scrollBy(1);
  };

  scrollToLeft = () => {
    this.setScrollAnimation(true);
    this.scrollBy(-1);
  };

  scrollBy = (indexOffset: number) => {
    const newOffsetQuantity = this.state.slideIndex + indexOffset;
    const offset = this.fullVisibleSlidesOnSegmentCount * (this.listItemWidth + this.slideMargin);
    const maxOffset = this.listNode.scrollWidth - this.listNode.clientWidth
      + parseInt(getComputedStyle(this.listNode).paddingRight, 10);

    const newOffset = Math.min(newOffsetQuantity * offset, maxOffset);

    this.setState({
      slideIndex: newOffsetQuantity,
      offset: newOffset * -1,
      isFirstSlide: newOffset === 0,
      isLastSlide: newOffset === maxOffset,
    });

    this.update();
  };

  update = () => {
    this.listNode.style.transform = `translateX(${this.state.offset}px)`;
    this.controllerPrev.disabled = this.state.isFirstSlide;
    this.controllerNext.disabled = this.state.isLastSlide;
  };

  setState = (state: CarouselState) => {
    this.state = {
      ...this.state,
      ...state,
    };
  };

  setScrollAnimation = (animated: boolean) => {
    if (animated) {
      this.listNode.style.transitionDuration = `${this.animationTime}s`;
    } else {
      this.listNode.style.transitionDuration = '0s';
    }
  };

  updateListNode = () => {
    this.listNode = document.querySelector(`.${this.listClassName}`) as HTMLElement;
  };

  updateSlides = () => {
    this.listItemsNodes = this.listNode.children;
  };

  updateSlidesCount = () => {
    this.slidesCount = Math.ceil(this.listItemsCount / this.visibleSlidesOnSegmentCount);
  };
}
